import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DemoDrills from './DemoDrills';
import SuperAdminNav from '../../components/SuperAdminNav';

const ROLE_LABELS = {
  coach: 'Coach',
  club_admin: 'Club Admin',
  club_staff: 'Club Staff',
  parent: 'Parent',
  other: 'Other',
};

const ORG_TYPE_LABELS = {
  club: 'Club',
  academy: 'Academy',
  school: 'School',
  rec_league: 'Rec League',
  other: 'Other',
};

const STATUS_LABELS = {
  active: 'Active',
  converted: 'Converted',
  expired: 'Expired',
  not_interested: 'Not Interested',
};

const NOTE_TYPE_LABELS = {
  email: 'Email',
  call: 'Call',
  text: 'Text',
  in_person: 'In Person',
  other: 'Other',
};

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function pagePathLabel(path) {
  if (!path) return 'Unknown page';
  if (path === '/admin' || path === '/admin/') return 'Dashboard';
  if (path.startsWith('/admin/roster')) return 'Roster';
  if (path.startsWith('/admin/drills')) return 'Drills';
  if (path.startsWith('/admin/leaderboard')) return 'Leaderboard';
  if (path.startsWith('/admin/reports')) return 'Reports';
  if (path.startsWith('/admin/billing')) return 'Billing';
  if (path.startsWith('/admin/library')) return 'Library';
  if (path.startsWith('/admin/player/')) return 'Player Profile';
  if (path.startsWith('/admin/communication')) return 'Communication';
  if (path.startsWith('/admin/challenges')) return 'Challenges';
  if (path === '/today' || path === '/') return 'Today (Player)';
  if (path === '/leaderboard') return 'Leaderboard (Player)';
  if (path === '/me') return 'Profile (Player)';
  if (path === '/faq') return 'FAQ';
  return path;
}

export default function ManageDemos() {
  const { apiFetch, token } = useAuth();


  const [activeTab, setActiveTab] = useState('accounts');
  const [contacts, setContacts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, converted: 0, expired: 0, not_interested: 0, logged_in: 0, not_logged_in: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Detail view
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactNotes, setContactNotes] = useState([]);
  const [contactActivity, setContactActivity] = useState([]);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    demo_number: '', contact_name: '', contact_email: '',
    role: 'coach', org_type: '', org_name: '', notes: '',
  });
  const [addError, setAddError] = useState('');

  // Add note form
  const [noteForm, setNoteForm] = useState({ note_type: 'email', note_text: '', contacted_at: new Date().toISOString().split('T')[0] });
  const [addingNote, setAddingNote] = useState(false);

  const loadContacts = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/demo-crm/contacts');
      setContacts(data.contacts || []);
    } catch (err) {
      console.error(err);
    }
  }, [apiFetch]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/demo-crm/summary');
      setSummary(data);
    } catch (err) {
      console.error(err);
    }
  }, [apiFetch]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadContacts(), loadSummary()]);
    } finally {
      setLoading(false);
    }
  }, [loadContacts, loadSummary]);

  useEffect(() => {
    if (activeTab === 'accounts') {
      loadAll();
    }
  }, [activeTab, loadAll]);

  const loadContactDetail = async (id) => {
    try {
      const data = await apiFetch(`/api/super/demo-crm/contacts/${id}/detail`);
      setSelectedContact(data.contact);
      setContactNotes(data.notes || []);
      setContactActivity(data.activity || []);
      setEditForm({
        contact_name: data.contact.contact_name || '',
        contact_email: data.contact.contact_email || '',
        role: data.contact.role || 'coach',
        org_type: data.contact.org_type || '',
        org_name: data.contact.org_name || '',
        status: data.contact.status || 'active',
        notes: data.contact.notes || '',
      });
      setSaveMsg('');
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddContact = async () => {
    setAddError('');
    if (!addForm.demo_number) { setAddError('Demo number is required'); return; }
    if (!addForm.contact_name.trim()) { setAddError('Contact name is required'); return; }
    try {
      await apiFetch('/api/super/demo-crm/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...addForm, demo_number: parseInt(addForm.demo_number, 10) }),
      });
      setShowAddModal(false);
      setAddForm({ demo_number: '', contact_name: '', contact_email: '', role: 'coach', org_type: '', org_name: '', notes: '' });
      loadAll();
    } catch (err) {
      setAddError(err.message || 'Failed to add contact');
    }
  };

  const handleSaveContact = async () => {
    if (!editForm || !selectedContact) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const updated = await apiFetch(`/api/super/demo-crm/contacts/${selectedContact.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      setSelectedContact({ ...selectedContact, ...updated });
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (err) {
      setSaveMsg('Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    if (!window.confirm(`Delete demo${selectedContact.demo_number} contact "${selectedContact.contact_name}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/super/demo-crm/contacts/${selectedContact.id}`, { method: 'DELETE' });
      setSelectedContact(null);
      loadAll();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddNote = async () => {
    if (!noteForm.note_text.trim()) return;
    setAddingNote(true);
    try {
      await apiFetch(`/api/super/demo-crm/contacts/${selectedContact.id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(noteForm),
      });
      setNoteForm({ note_type: 'email', note_text: '', contacted_at: new Date().toISOString().split('T')[0] });
      // Reload detail
      const data = await apiFetch(`/api/super/demo-crm/contacts/${selectedContact.id}/detail`);
      setContactNotes(data.notes || []);
    } catch (err) {
      console.error(err);
    } finally {
      setAddingNote(false);
    }
  };

  const handleExport = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${API_BASE}/api/super/demo-crm/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'demo-accounts.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const handleBackToList = () => {
    setSelectedContact(null);
    setContactNotes([]);
    setContactActivity([]);
    setEditForm(null);
    loadAll();
  };

  const filteredContacts = contacts.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (searchQuery.trim()) {
      let term = searchQuery.trim();
      if (term.startsWith('"') && term.endsWith('"') && term.length > 1) {
        term = term.slice(1, -1);
      }
      if (!term) return true;
      const lowerTerm = term.toLowerCase();
      const email = `demo${c.demo_number}@traindaily.com`;
      return email.toLowerCase().includes(lowerTerm)
        || (c.contact_name || '').toLowerCase().includes(lowerTerm)
        || (c.contact_email || '').toLowerCase().includes(lowerTerm)
        || (c.org_name || '').toLowerCase().includes(lowerTerm);
    }
    return true;
  });

  const statusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-demo-active';
      case 'converted': return 'status-demo-converted';
      case 'expired': return 'status-demo-expired';
      case 'not_interested': return 'status-demo-not-interested';
      default: return 'status-demo-active';
    }
  };

  if (loading && activeTab === 'accounts') {
    return (
      <div className="admin-layout">
        <SuperAdminNav activeItem="demos" />
        <div className="loading-spinner" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <SuperAdminNav activeItem="demos" />

      <div className="admin-page" style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button
            className={`btn btn-sm ${activeTab === 'accounts' ? 'btn-orange' : 'btn-outline'}`}
            onClick={() => setActiveTab('accounts')}
          >Demo Accounts</button>
          <button
            className={`btn btn-sm ${activeTab === 'drills' ? 'btn-orange' : 'btn-outline'}`}
            onClick={() => setActiveTab('drills')}
          >Demo Drills</button>
        </div>

        {activeTab === 'drills' && <DemoDrills embedded />}

        {activeTab === 'accounts' && !selectedContact && (
          <>
            {/* Dashboard summary */}
            <div className="super-totals">
              <div className="super-total-card">
                <div className="super-total-value">{summary.active}</div>
                <div className="super-total-label">Active</div>
              </div>
              <div className="super-total-card">
                <div className="super-total-value">{summary.logged_in}</div>
                <div className="super-total-label">Logged In</div>
              </div>
              <div className="super-total-card">
                <div className="super-total-value">{summary.not_logged_in}</div>
                <div className="super-total-label">Not Logged In</div>
              </div>
              <div className="super-total-card">
                <div className="super-total-value">{summary.converted}</div>
                <div className="super-total-label">Converted</div>
              </div>
            </div>

            {/* Action bar */}
            <div className="demo-crm-actions">
              <button className="btn btn-sm btn-blue" onClick={() => setShowAddModal(true)}>+ Add Demo</button>
              <button className="btn btn-sm btn-outline" onClick={handleExport}>Export CSV</button>
              <select
                className="demo-crm-status-select"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="converted">Converted</option>
                <option value="expired">Expired</option>
                <option value="not_interested">Not Interested</option>
              </select>
              <input
                className="form-input"
                type="text"
                placeholder="Search name, email, club..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ maxWidth: 220, padding: '6px 10px', fontSize: '0.85rem' }}
              />
            </div>

            {/* Contact list */}
            {filteredContacts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                {contacts.length === 0 ? 'No demo accounts tracked yet. Click "+ Add Demo" to get started.' : 'No contacts match the current filter.'}
              </div>
            ) : (
              <table className="super-table">
                <thead>
                  <tr>
                    <th>Demo Email</th>
                    <th>Contact</th>
                    <th>Date Added</th>
                    <th>Role</th>
                    <th>Organization</th>
                    <th>Status</th>
                    <th>Last Activity</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map(c => (
                    <tr key={c.id} className="super-table-row" onClick={() => loadContactDetail(c.id)}>
                      <td style={{ whiteSpace: 'nowrap' }}>demo{c.demo_number}@traindaily.com</td>
                      <td className="super-account-name">{c.contact_name}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(c.given_at)}</td>
                      <td>{ROLE_LABELS[c.role] || c.role}</td>
                      <td>{c.org_name || '-'}</td>
                      <td><span className={`status-badge ${statusBadgeClass(c.status)}`}>{STATUS_LABELS[c.status] || c.status}</span></td>
                      <td>{timeAgo(c.last_activity_at)}</td>
                      <td className="super-arrow">&#8250;</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* Detail view */}
        {activeTab === 'accounts' && selectedContact && editForm && (
          <div>
            <button className="btn btn-sm btn-outline" onClick={handleBackToList} style={{ marginBottom: 16 }}>
              &#8592; Back to List
            </button>

            <div className="demo-crm-detail">
              <div className="demo-crm-detail-header">
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.2rem' }}>{selectedContact.contact_name}</h2>
                  <div style={{ color: '#888', fontSize: '0.85rem', marginTop: 4 }}>demo{selectedContact.demo_number}@traindaily.com</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className={`status-badge ${statusBadgeClass(editForm.status)}`}>{STATUS_LABELS[editForm.status] || editForm.status}</span>
                  <button className="btn btn-sm btn-danger" onClick={handleDeleteContact}>Delete</button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 20 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Contact Name</label>
                  <input className="form-input" value={editForm.contact_name} onChange={e => setEditForm({ ...editForm, contact_name: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Contact Email</label>
                  <input className="form-input" type="email" value={editForm.contact_email} onChange={e => setEditForm({ ...editForm, contact_email: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Role</label>
                  <select className="form-input" value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Status</label>
                  <select className="form-input" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Organization Type</label>
                  <select className="form-input" value={editForm.org_type} onChange={e => setEditForm({ ...editForm, org_type: e.target.value })}>
                    <option value="">--</option>
                    {Object.entries(ORG_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Organization Name</label>
                  <input className="form-input" value={editForm.org_name} onChange={e => setEditForm({ ...editForm, org_name: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" rows={2} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <button className="btn btn-sm btn-orange" onClick={handleSaveContact} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
                {saveMsg && <span style={{ fontSize: '0.85rem', color: saveMsg === 'Saved' ? '#22c55e' : '#ef4444' }}>{saveMsg}</span>}
              </div>

              <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: 4 }}>
                Given: {formatDate(selectedContact.given_at)} &middot; Last Login: {selectedContact.last_login_at ? formatDateTime(selectedContact.last_login_at) : 'Never'}
              </div>
            </div>

            {/* Contact Log */}
            <div className="demo-crm-section">
              <h3>Contact Log</h3>

              <div className="demo-crm-note" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <select className="form-input" style={{ width: 120, padding: '8px 10px', fontSize: '0.85rem' }} value={noteForm.note_type} onChange={e => setNoteForm({ ...noteForm, note_type: e.target.value })}>
                    {Object.entries(NOTE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <input type="date" className="form-input" style={{ width: 150, padding: '8px 10px', fontSize: '0.85rem' }} value={noteForm.contacted_at} onChange={e => setNoteForm({ ...noteForm, contacted_at: e.target.value })} />
                </div>
                <textarea
                  className="form-textarea"
                  rows={2}
                  placeholder="Add a note..."
                  value={noteForm.note_text}
                  onChange={e => setNoteForm({ ...noteForm, note_text: e.target.value })}
                  style={{ fontSize: '0.85rem' }}
                />
                <div style={{ textAlign: 'right', marginTop: 8 }}>
                  <button className="btn btn-sm btn-blue" onClick={handleAddNote} disabled={addingNote || !noteForm.note_text.trim()}>
                    {addingNote ? 'Adding...' : 'Add Note'}
                  </button>
                </div>
              </div>

              {contactNotes.length === 0 ? (
                <div style={{ padding: 16, color: '#888', fontSize: '0.85rem', textAlign: 'center' }}>No notes yet.</div>
              ) : (
                contactNotes.map(n => (
                  <div key={n.id} className="demo-crm-note">
                    <div className="demo-crm-note-header">
                      <span className="demo-crm-note-type">{NOTE_TYPE_LABELS[n.note_type] || n.note_type}</span>
                      <span className="demo-crm-note-date">{formatDate(n.contacted_at)}</span>
                    </div>
                    <div className="demo-crm-note-text">{n.note_text}</div>
                  </div>
                ))
              )}
            </div>

            {/* Activity Timeline */}
            <div className="demo-crm-section">
              <h3>Activity Timeline</h3>
              {contactActivity.length === 0 ? (
                <div style={{ padding: 16, color: '#888', fontSize: '0.85rem', textAlign: 'center' }}>No activity recorded yet.</div>
              ) : (
                <div className="demo-crm-timeline">
                  {contactActivity.map(a => (
                    <div key={a.id} className="demo-crm-timeline-item">
                      <div className="demo-crm-timeline-icon">
                        {a.event_type === 'login' ? '\u{1F511}' : '\u{1F4C4}'}
                      </div>
                      <div className="demo-crm-timeline-content">
                        {a.event_type === 'login'
                          ? `${a.user_type === 'coach' ? 'Coach' : 'Player'} logged in`
                          : `${a.user_type === 'coach' ? 'Coach' : 'Player'} viewed ${pagePathLabel(a.page_path)}`
                        }
                      </div>
                      <div className="demo-crm-timeline-time">{formatDateTime(a.created_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h2 style={{ marginTop: 0 }}>Add Demo Account</h2>
            {addError && <div style={{ color: '#ef4444', marginBottom: 12, fontSize: '0.85rem' }}>{addError}</div>}

            <div className="form-group">
              <label className="form-label">Demo Number</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#888', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>demo</span>
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  max="999"
                  value={addForm.demo_number}
                  onChange={e => setAddForm({ ...addForm, demo_number: e.target.value })}
                  placeholder="4"
                  style={{ width: 80 }}
                />
                <span style={{ color: '#888', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>@traindaily.com</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Contact Name *</label>
              <input className="form-input" value={addForm.contact_name} onChange={e => setAddForm({ ...addForm, contact_name: e.target.value })} placeholder="Juan Garcia" />
            </div>

            <div className="form-group">
              <label className="form-label">Contact Email</label>
              <input className="form-input" type="email" value={addForm.contact_email} onChange={e => setAddForm({ ...addForm, contact_email: e.target.value })} placeholder="juan@example.com" />
            </div>

            <div className="form-row">
              <div className="form-group form-group-half">
                <label className="form-label">Role</label>
                <select className="form-input" value={addForm.role} onChange={e => setAddForm({ ...addForm, role: e.target.value })}>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-group form-group-half">
                <label className="form-label">Organization Type</label>
                <select className="form-input" value={addForm.org_type} onChange={e => setAddForm({ ...addForm, org_type: e.target.value })}>
                  <option value="">--</option>
                  {Object.entries(ORG_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Organization Name</label>
              <input className="form-input" value={addForm.org_name} onChange={e => setAddForm({ ...addForm, org_name: e.target.value })} placeholder="FC Example" />
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" rows={2} value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} placeholder="Optional notes..." />
            </div>

            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn btn-orange" onClick={handleAddContact}>Add Demo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
